<template>
  <div class="show-label-settings" v-if="visible">
    <hr />
    <div class="flex-row space-between">
      <div>
        <h3>{{ _('Show labels') }}</h3>
        <p class="help-block">{{ _('Labels such as Premiere or Preview are shown to customers next to the show time. Choose them for each show in the showtimes calendar.') }}</p>
      </div>
      <button class="btn btn-success addbutton" type="button" v-on:click="editLabel(null)"><i class="fa fa-fw fa-plus"></i> {{ _('Create new...') }}</button>
    </div>
    <p v-if="!labels.length" class="text-muted">{{ _('No show labels yet.') }}</p>
    <draggable v-else class="list-group show-label-list" v-model="labels" item-key="id" handle=".show-label-handle" v-on:end="saveOrder">
      <div v-for="label in labels" :key="label.id" class="list-group-item flex-row space-between">
        <div>
          <i class="fa fa-fw fa-bars show-label-handle" :title="_('Drag to reorder')"></i>
          <span class="show-label-pill" :style="showLabelStyle(label)">{{ label.name }}</span>
          <small class="text-muted show-label-count">{{ showCountText(label) }}</small>
        </div>
        <div class="button-col">
          <button type="button" class="btn btn-default btn-sm" :title="_('Edit')" v-on:click="editLabel(label)"><i class="fa fa-fw fa-pencil"></i></button>
          <button type="button" class="btn btn-danger btn-sm" :title="_('Delete')" v-on:click="confirmDelete(label)"><i class="fa fa-fw fa-trash"></i></button>
        </div>
      </div>
    </draggable>
  </div>
</template>

<script>
import axios from 'axios'
import errors from '~/lib/errors.js'
import modals from '~/lib/modals'
import { showLabelStyle } from '~/lib/showLabels'
import { ShowLabelEditor } from '#components'

export default {
  props: ['baseDomain', 'shopId', 'apiKey'],
  mixins: [errors, modals],
  data () {
    return {
      labels: [],
      hasEvents: false
    }
  },
  computed: {
    // Only shops that sell shows need labels, but a shop that already has labels keeps the list
    visible () {
      return this.hasEvents || this.labels.length > 0
    }
  },
  methods: {
    showLabelStyle (label) {
      return showLabelStyle(label)
    },
    showCountText (label) {
      const count = label.show_count || 0
      return count === 1 ? this._('1 upcoming show') : this._('%s upcoming shows').replace('%s', count)
    },
    async load () {
      let response
      try {
        response = await axios.get(this.baseDomain + this.shopId + '/showlabels.json?showcounts=true&apikey=' + this.apiKey)
      } catch (e) {
        return this.showError(e)
      }
      this.labels = response.data
    },
    async checkEvents () {
      let response
      try {
        response = await axios.get(this.baseDomain + this.shopId + '/products.json?sSearch_3=1-4-all&simple=true&iDisplayStart=0&iDisplayLength=1&apikey=' + this.apiKey)
      } catch (e) {
        return
      }
      this.hasEvents = Array.isArray(response.data) && response.data.length > 0
    },
    editLabel (label) {
      this.showModal('showlabeleditor', ShowLabelEditor, {
        props: {
          baseDomain: this.baseDomain,
          shopId: this.shopId,
          apiKey: this.apiKey,
          labelId: label ? label.id : null
        },
        events: {
          saved: this.load
        },
        width: 600
      })
    },
    async saveOrder () {
      try {
        for (let i = 0; i < this.labels.length; i++) {
          if (parseInt(this.labels[i].sortorder) !== i + 1) {
            await axios.put(this.baseDomain + this.shopId + '/showlabels/' + this.labels[i].id + '.json?apikey=' + this.apiKey, { id: this.labels[i].id, sortorder: i + 1 })
            this.labels[i].sortorder = i + 1
          }
        }
      } catch (e) {
        this.showError(e)
        this.load()
      }
    },
    confirmDelete (label) {
      const count = label.show_count || 0
      this.showDialog({
        title: this._('Delete the show label %s?').replace('%s', label.name),
        fields: count ? [{ type: 'info', text: this._('The label is removed from all shows, including %s upcoming shows.').replace('%s', count) }] : [],
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'close',
            handler: () => {}
          },
          {
            title: this._('Delete'),
            class: 'danger',
            icon: 'trash',
            handler: () => this.deleteLabel(label)
          }
        ]
      })
    },
    async deleteLabel (label) {
      try {
        await axios.delete(this.baseDomain + this.shopId + '/showlabels/' + label.id + '.json?apikey=' + this.apiKey)
      } catch (e) {
        return this.showError(e)
      }
      this.load()
    }
  },
  mounted () {
    this.load()
    this.checkEvents()
  }
}
</script>

<style>
.show-label-settings .show-label-list {
  max-width: 640px;
}
.show-label-settings .show-label-handle {
  cursor: move;
  color: #999;
}
.show-label-settings .show-label-pill {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 700;
}
.show-label-settings .show-label-count {
  margin-left: 8px;
}
</style>
